/**
 * Badge components for multi-dimensional finding metrics
 * Related change: finding-metrics-canonical
 */

import {
  AlertTriangle,
  XCircle,
  Info,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  MinusCircle,
  Ban,
  User,
  ClipboardCheck,
  Palette,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ChipTone } from "@/components/ui/chip";
import { SimpleTooltip } from "@/components/ui/tooltip";

// ============================================================
// Types
// ============================================================

type FindingType = "helps" | "hinders" | "missing" | "irrelevant" | "incorrect";
type ComplianceImpact = "critical" | "major" | "minor" | "informational";
type TaskImpact =
  | "blocks"
  | "impairs"
  | "slows"
  | "neutral"
  | "accelerates"
  | "enables";
type Confidence = "high" | "medium" | "low";

// ============================================================
// StatusBadge - Finding type with status-specific styling
// ============================================================

interface StatusBadgeProps {
  type: FindingType;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const typeConfig: Record<
  FindingType,
  {
    label: string;
    icon: LucideIcon;
    styles: string;
  }
> = {
  helps: {
    label: "Strength",
    icon: Sparkles,
    styles: "bg-transparent text-finding-helps border-finding-helps/30",
  },
  hinders: {
    label: "Issue",
    icon: AlertTriangle,
    styles: "bg-transparent text-finding-hinders border-finding-hinders/30",
  },
  missing: {
    label: "Gap",
    icon: XCircle,
    styles: "bg-transparent text-finding-missing border-finding-missing/40",
  },
  irrelevant: {
    label: "Nit",
    icon: Info,
    styles: "bg-transparent text-muted-foreground border-border",
  },
  incorrect: {
    label: "Incorrect",
    icon: Ban,
    styles: "bg-transparent text-severity-minor border-severity-minor/30",
  },
};

const sizeClasses = {
  sm: "px-1.5 py-0.5 text-xs gap-1 [&>svg]:size-3",
  md: "px-2 py-0.5 text-xs gap-1.5 [&>svg]:size-3.5",
  lg: "px-3 py-1 text-sm gap-2 [&>svg]:size-4",
} as const;

export function StatusBadge({
  type,
  size = "md",
  className,
}: StatusBadgeProps) {
  const config = typeConfig[type];
  const Icon = config.icon;

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-sm font-medium border whitespace-nowrap",
        config.styles,
        sizeClasses[size],
        className,
      )}
    >
      <Icon />
      {config.label}
    </span>
  );
}

// ============================================================
// ComplianceImpactBadge
// ============================================================

interface ComplianceImpactBadgeProps {
  impact: ComplianceImpact;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const complianceConfig: Record<
  ComplianceImpact,
  {
    label: string;
    styles: string;
  }
> = {
  critical: {
    label: "Critical",
    styles:
      "bg-transparent text-severity-critical border-severity-critical/30 font-semibold",
  },
  major: {
    label: "Major",
    styles:
      "bg-transparent text-severity-important border-severity-important/30",
  },
  minor: {
    label: "Minor",
    styles: "bg-transparent text-severity-minor border-severity-minor/30",
  },
  informational: {
    label: "Info",
    styles: "bg-transparent text-muted-foreground border-border",
  },
};

export function ComplianceImpactBadge({
  impact,
  size = "md",
  className,
}: ComplianceImpactBadgeProps) {
  const config = complianceConfig[impact];

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-sm font-medium border whitespace-nowrap",
        config.styles,
        sizeClasses[size],
        className,
      )}
    >
      {config.label}
    </span>
  );
}

// ============================================================
// TaskImpactBadge
// ============================================================

interface TaskImpactBadgeProps {
  impact: TaskImpact;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const taskImpactConfig: Record<
  TaskImpact,
  {
    label: string;
    styles: string;
  }
> = {
  blocks: {
    label: "Blocks",
    styles:
      "bg-transparent text-severity-critical border-severity-critical/30 font-semibold",
  },
  impairs: {
    label: "Impairs",
    styles:
      "bg-transparent text-severity-important border-severity-important/30",
  },
  slows: {
    label: "Slows",
    styles:
      "bg-transparent text-severity-important border-severity-important/20",
  },
  neutral: {
    label: "Neutral",
    styles: "bg-transparent text-muted-foreground border-border",
  },
  accelerates: {
    label: "Accelerates",
    styles: "bg-transparent text-finding-helps border-finding-helps/30",
  },
  enables: {
    label: "Enables",
    styles:
      "bg-transparent text-finding-helps border-finding-helps/30 font-semibold",
  },
};

export function TaskImpactBadge({
  impact,
  size = "md",
  className,
}: TaskImpactBadgeProps) {
  const config = taskImpactConfig[impact];

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-sm font-medium border whitespace-nowrap",
        config.styles,
        sizeClasses[size],
        className,
      )}
    >
      {config.label}
    </span>
  );
}

// ============================================================
// ConfidenceBadge with tooltip
// ============================================================

interface ConfidenceBadgeProps {
  confidence: Confidence;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const confidenceConfig: Record<
  Confidence,
  {
    label: string;
    description: string;
    styles: string;
  }
> = {
  high: {
    label: "High",
    description: "Strong evidence and clear domain alignment",
    styles: "bg-transparent text-finding-helps border-finding-helps/30",
  },
  medium: {
    label: "Medium",
    description: "Some evidence, may require validation",
    styles:
      "bg-transparent text-severity-important border-severity-important/30",
  },
  low: {
    label: "Low",
    description: "Limited evidence, needs further investigation",
    styles: "bg-transparent text-muted-foreground border-border",
  },
};

export function ConfidenceBadge({
  confidence,
  size = "md",
  className,
}: ConfidenceBadgeProps) {
  const config = confidenceConfig[confidence];

  return (
    <SimpleTooltip content={<p className="text-sm">{config.description}</p>}>
      <span
        role="button"
        tabIndex={0}
        className={cn(
          "inline-flex items-center rounded-full font-medium border whitespace-nowrap cursor-help gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          config.styles,
          sizeClasses[size],
          className,
        )}
      >
        <HelpCircle aria-hidden="true" />
        {config.label}
      </span>
    </SimpleTooltip>
  );
}

// ============================================================
// FixStatusBadge - finding fix lifecycle status
// ============================================================

type FixStatus =
  | "open"
  | "fix_proposed"
  | "fix_applied"
  | "verified_resolved"
  | "verified_persists"
  | "wont_fix";

interface FixStatusBadgeProps {
  status: FixStatus;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const fixStatusConfig: Record<
  FixStatus,
  {
    label: string;
    styles: string;
  }
> = {
  open: {
    label: "Open",
    styles: "bg-transparent text-muted-foreground border-border",
  },
  fix_proposed: {
    label: "Fix proposed",
    styles: "bg-transparent text-primary border-primary/30",
  },
  fix_applied: {
    label: "Fix applied",
    styles: "bg-transparent text-primary border-primary/40",
  },
  verified_resolved: {
    label: "Resolved",
    styles: "bg-transparent text-finding-helps border-finding-helps/30",
  },
  verified_persists: {
    label: "Persists",
    styles: "bg-transparent text-severity-critical border-severity-critical/30",
  },
  wont_fix: {
    label: "Won't fix",
    styles: "bg-transparent text-muted-foreground border-border",
  },
};

export function FixStatusBadge({
  status,
  size = "md",
  className,
}: FixStatusBadgeProps) {
  const config = fixStatusConfig[status];
  if (!config) return null;

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-sm font-medium border whitespace-nowrap",
        config.styles,
        sizeClasses[size],
        className,
      )}
    >
      {config.label}
    </span>
  );
}

// ============================================================
// Legacy SeverityBadge for backward compatibility
// ============================================================

type Severity = "critical" | "major" | "minor" | "nit";

interface SeverityBadgeProps {
  severity: Severity;
  size?: "sm" | "md" | "lg";
  className?: string;
  deprecated?: boolean;
}

const severityConfig: Record<
  Severity,
  {
    label: string;
    styles: string;
  }
> = {
  critical: {
    label: "Critical",
    styles: "bg-transparent text-severity-critical border-severity-critical/30",
  },
  major: {
    label: "Major",
    styles:
      "bg-transparent text-severity-important border-severity-important/30",
  },
  minor: {
    label: "Minor",
    styles: "bg-transparent text-severity-minor border-severity-minor/30",
  },
  nit: {
    label: "Nit",
    styles: "bg-transparent text-muted-foreground border-border",
  },
};

export function SeverityBadge({
  severity,
  size = "md",
  className,
  deprecated = false,
}: SeverityBadgeProps) {
  const config = severityConfig[severity];

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-sm font-medium border whitespace-nowrap",
        config.styles,
        sizeClasses[size],
        className,
      )}
    >
      {config.label}
      {deprecated && " (legacy)"}
    </span>
  );
}

// ============================================================
// Exported color maps — import these instead of redefining locally
// ============================================================

/** Inline text colors for finding types (used outside of badge context) */
export const findingTypeColorMap: Record<
  FindingType,
  {
    icon: LucideIcon;
    color: string;
    label: string;
  }
> = {
  helps: {
    icon: ThumbsUp,
    color: "text-finding-helps",
    label: "Helps",
  },
  hinders: {
    icon: ThumbsDown,
    color: "text-finding-hinders",
    label: "Hinders",
  },
  missing: {
    icon: HelpCircle,
    color: "text-finding-missing",
    label: "Missing",
  },
  irrelevant: {
    icon: MinusCircle,
    color: "text-muted-foreground",
    label: "Irrelevant",
  },
  incorrect: {
    icon: Ban,
    color: "text-severity-minor",
    label: "Incorrect",
  },
};

/** Inline badge colors for severity (used outside of SeverityBadge context) */
export const severityColorMap: Record<
  Severity,
  {
    label: string;
    color: string;
    border: string;
  }
> = {
  critical: {
    label: "Critical",
    color:
      "bg-transparent text-severity-critical border border-severity-critical/30",
    border: "border-l-severity-critical",
  },
  major: {
    label: "Major",
    color:
      "bg-transparent text-severity-important border border-severity-important/30",
    border: "border-l-severity-important",
  },
  minor: {
    label: "Minor",
    color: "bg-transparent text-severity-minor border border-severity-minor/30",
    border: "border-l-severity-minor",
  },
  nit: {
    label: "Nit",
    color: "bg-transparent text-muted-foreground border border-border",
    border: "border-l-border",
  },
};

/** Text-only severity colors for inline use (no background) — WCAG AAA compliant */
export const severityTextColorMap: Record<Severity, string> = {
  critical: "text-severity-critical",
  major: "text-severity-important",
  minor: "text-severity-minor",
  nit: "text-muted-foreground",
};

/** Display order for severity levels (most severe first) */
export const SEVERITY_ORDER: readonly Severity[] = [
  "critical",
  "major",
  "minor",
  "nit",
] as const;

export type CanonicalStatus = "open" | "addressed" | "dismissed";

/**
 * Coarse three-way grouping of a canonical's lifecycle for filters and
 * legacy props. Accepts both lifecycle states (`fix_applied`, `snoozed`,
 * …) and the legacy status values.
 */
export function normalizeCanonicalStatus(status: string): CanonicalStatus {
  switch (status) {
    case "addressed":
    case "verified":
    case "fix_applied":
      return "addressed";
    case "dismissed":
    case "deferred":
    case "wont_fix":
    case "snoozed":
    case "risk_accepted":
      return "dismissed";
    default:
      return "open";
  }
}

/** Lifecycle of a canonical finding (`canonical_findings.state`). */
export type CanonicalState =
  | "new"
  | "open"
  | "in_progress"
  | "fix_applied"
  | "verified"
  | "regressed"
  | "snoozed"
  | "risk_accepted"
  | "dismissed";

/** States that count as open in every queue (regressed acts like open). */
export const OPEN_STATES: readonly CanonicalState[] = [
  "new",
  "open",
  "in_progress",
  "regressed",
];

/**
 * Still counts against quality (CONTRACT decision 1): open-ish, or a fix
 * applied that no re-run has verified yet. Accepts lifecycle states and
 * legacy status / triage values.
 */
export function isUnresolved(stateOrStatus: string | null | undefined) {
  switch (stateOrStatus ?? "open") {
    case "new":
    case "open":
    case "in_progress":
    case "regressed":
    case "fix_applied":
    case "addressed":
      return true;
    default:
      return false;
  }
}

const DISMISS_REASON_LABEL: Record<string, string> = {
  not_relevant: "Not relevant",
  walker_error: "Walker got it wrong",
  duplicate: "Duplicate",
};

/**
 * The chip a lifecycle state shows, or null for plain open. Words carry
 * the meaning; tone only reinforces it. `fix_applied` reads "Awaiting
 * re-run": only a re-run verifies a fix.
 */
export function canonicalStateChip(
  state: string | null | undefined,
  reason?: string | null,
): { label: string; tone: ChipTone } | null {
  switch (state) {
    case "new":
      return { label: "New", tone: "primary" };
    case "open":
      if (reason === "fix_didnt_hold")
        return { label: "Fix didn't hold", tone: "critical" };
      if (reason === "resurfaced")
        return { label: "Resurfaced", tone: "important" };
      return null;
    case "in_progress":
      return { label: "In progress", tone: "primary" };
    case "fix_applied":
      return { label: "Awaiting re-run", tone: "primary" };
    case "verified":
      return { label: "Verified", tone: "helps" };
    case "regressed":
      return { label: "Regressed", tone: "critical" };
    case "snoozed":
      return { label: "Snoozed", tone: "muted" };
    case "risk_accepted":
      return { label: "Risk accepted", tone: "muted" };
    case "dismissed":
      return {
        label: DISMISS_REASON_LABEL[reason ?? ""] ?? "Dismissed",
        tone: "muted",
      };
    default:
      return null;
  }
}

export const canonicalStatusMap: Record<
  CanonicalStatus,
  {
    label: string;
    color: string;
  }
> = {
  open: {
    label: "Open",
    color: "bg-transparent text-muted-foreground border border-border",
  },
  addressed: {
    label: "Fixed",
    color: "bg-transparent text-finding-helps border border-finding-helps/30",
  },
  dismissed: {
    label: "Dismissed",
    color: "bg-transparent text-muted-foreground border border-border",
  },
};

export type AnalysisLens = "persona" | "domain_expert" | "designer";

export const lensColorMap: Record<
  AnalysisLens,
  {
    label: string;
    icon: typeof User;
    color: string;
  }
> = {
  persona: {
    label: "Persona",
    icon: User,
    color: "text-blue-600 dark:text-blue-400",
  },
  domain_expert: {
    label: "Domain Expert",
    icon: ClipboardCheck,
    color: "text-amber-600 dark:text-amber-400",
  },
  designer: {
    label: "Designer",
    icon: Palette,
    color: "text-purple-600 dark:text-purple-400",
  },
};

// ============================================================
// DesignSystemCitationBadge
// ============================================================

interface DesignSystemCitationBadgeProps {
  sourceName: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function DesignSystemCitationBadge({
  sourceName,
  size = "sm",
  className,
}: DesignSystemCitationBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-medium border whitespace-nowrap",
        "bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-950 dark:text-violet-200 dark:border-violet-800",
        sizeClasses[size],
        className,
      )}
    >
      <Palette />
      {sourceName}
    </span>
  );
}

// ============================================================
// TriageStatusBadge - triage status for raw observations
// ============================================================

export type TriageStatus = "open" | "addressed" | "dismissed";

/** Normalize legacy DB values to simplified statuses */
export function normalizeTriageStatus(status: string): TriageStatus {
  if (status === "addressed") return "addressed";
  if (status === "dismissed" || status === "deferred" || status === "wont_fix")
    return "dismissed";
  return "open";
}

export const triageStatusConfig: Record<
  TriageStatus,
  {
    label: string;
    styles: string;
  }
> = {
  open: {
    label: "Open",
    styles: "bg-transparent text-muted-foreground border-border",
  },
  addressed: {
    label: "Fixed",
    styles: "bg-transparent text-finding-helps border-finding-helps/30",
  },
  dismissed: {
    label: "Dismissed",
    styles: "bg-transparent text-muted-foreground border-border",
  },
};

/** All manually-settable triage statuses in display order */
export const TRIAGE_STATUS_OPTIONS: TriageStatus[] = [
  "open",
  "addressed",
  "dismissed",
];

interface TriageStatusBadgeProps {
  /** Accepts legacy DB values — they are normalized for display */
  status: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function TriageStatusBadge({
  status,
  size = "sm",
  className,
}: TriageStatusBadgeProps) {
  const normalized = normalizeTriageStatus(status);
  const config = triageStatusConfig[normalized];
  if (!config) return null;

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-sm font-medium border whitespace-nowrap",
        config.styles,
        sizeClasses[size],
        className,
      )}
    >
      {config.label}
    </span>
  );
}

/** Re-export types for consumers */
export type { FindingType, Severity };
